import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useJobPostDetail } from "@/hooks/useJobPosts";
import { useAuth } from "@/hooks/useAuth";
import { useSavedJobs } from "@/hooks/useSavedJobs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, UserPlus } from "lucide-react";
import {
  isCrawlerJob,
  isExternalJob,
  getCompanyName,
  getSourceName,
  getSourceLabel,
} from "@/lib/jobUtils";
import { BASE_URL } from "@/lib/constants";
import { JobDetailHeader } from "@/components/jobs/JobDetailHeader";
import { JobDetailContent } from "@/components/jobs/JobDetailContent";
import { JobDetailSidebar } from "@/components/jobs/JobDetailSidebar";
import { StickyMobileCTA } from "@/components/jobs/StickyMobileCTA";

const OG_IMAGE = `${BASE_URL}/og-oportunidades.png`;

const employmentTypeLabels: Record<string, string> = {
  estagio: "Estágio", clt: "CLT", pj: "PJ", freelancer: "Freelancer",
  aprendiz: "Aprendiz", junior: "Júnior",
  primeiro_emprego: "Primeiro Emprego", assistente: "Assistente",
  analista_junior: "Analista Junior",
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function formatPubDate(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function OportunidadeDetalhe() {
  const { id } = useParams<{ id: string }>();
  const { data: job, isLoading } = useJobPostDetail(id);
  const { user, loading: authLoading } = useAuth();
  const { isSaved, toggleSave } = useSavedJobs();

  const { data: existingApplication } = useQuery({
    queryKey: ["user_application_check", id, user?.id],
    enabled: !!user && !!id,
    queryFn: async () => {
      const { data } = await supabase
        .from("job_applications")
        .select("id")
        .eq("job_id", id!)
        .eq("user_id", user!.id)
        .limit(1);
      return data && data.length > 0 ? data[0] : null;
    },
  });

  if (isLoading) {
    return (
      <div className="container px-4 md:px-6 py-8 max-w-5xl mx-auto">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-64 w-full mb-6" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container px-4 md:px-6 py-16 text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">Vaga não encontrada</h1>
        <p className="text-muted-foreground mb-6">Esta oportunidade pode ter sido encerrada ou não existe mais.</p>
        <Button asChild><Link to="/oportunidades">Ver outras oportunidades</Link></Button>
      </div>
    );
  }

  const companyName = getCompanyName(job);
  const isCrawler = isCrawlerJob(job);
  const isExternal = isExternalJob(job);
  const sourceName = getSourceName(job);
  const employmentLabel = employmentTypeLabels[job.employment_type] || job.employment_type;
  const location = [job.location_city, job.location_state].filter(Boolean).join(", ");

  const nextParam = `/oportunidades/${job.id}`;

  // Determine if content is "rich" (internal job) or "lean" (crawler with few fields)
  const hasRichContent = Boolean(
    (job.responsibilities as string[])?.length ||
    (job.requirements_must as string[])?.length ||
    (job.requirements_nice as string[])?.length ||
    (job.benefits as string[])?.length ||
    (job.process_steps as string[])?.length
  );

  const seoTitle = `${job.title} em ${companyName} | Corre Digital Talent`;
  const seoDesc = job.description
    ? `${job.description.slice(0, 150).trim()}…`
    : `Vaga de ${employmentLabel}${location ? ` em ${location}` : ""} na ${companyName}. Cadastre-se no Corre Talent e candidate-se!`;
  const canonical = `${BASE_URL}/oportunidades/${job.id}`;

  return (
    <div className="container px-4 md:px-6 py-8 max-w-5xl mx-auto pb-24 lg:pb-8">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="Corre Digital Talent" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "JobPosting",
          title: job.title,
          description: job.description || seoDesc,
          hiringOrganization: { "@type": "Organization", name: companyName },
          jobLocation: location ? { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: job.location_city, addressRegion: job.location_state, addressCountry: "BR" } } : undefined,
          employmentType: job.employment_type?.toUpperCase(),
          datePosted: job.published_at,
          validThrough: job.deadline_date,
          baseSalary: job.stipend_min ? { "@type": "MonetaryAmount", currency: "BRL", value: { "@type": "QuantitativeValue", minValue: job.stipend_min, maxValue: job.stipend_max || job.stipend_min, unitText: "MONTH" } } : undefined,
          url: canonical,
        })}</script>
      </Helmet>

      {/* Back link */}
      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="mb-6">
        <Link to="/oportunidades" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />Voltar para oportunidades
        </Link>
      </motion.div>

      {/* Anonymous top banner */}
      {!user && !authLoading && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-primary/5 border border-primary/15 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <UserPlus className="h-5 w-5 text-primary shrink-0" />
            <p className="text-sm text-foreground">
              {isCrawler
                ? <><strong>Vaga do {getSourceLabel(sourceName)}</strong>. Crie sua conta para acompanhar.</>
                : <><strong>Para se candidatar</strong>, crie sua conta gratuita no Corre Talent</>
              }
            </p>
          </div>
          <Button asChild size="sm" className="shrink-0">
            <Link to={`/aluno/escolha?next=${encodeURIComponent(nextParam)}`}>Criar conta grátis</Link>
          </Button>
        </motion.div>
      )}

      {/* Adaptive layout: 2-col for rich content, single-col for lean crawler jobs */}
      {hasRichContent ? (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <Card>
              <CardContent className="p-6">
                <JobDetailHeader job={job} />
              </CardContent>
            </Card>
            <JobDetailContent job={job} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="hidden lg:block">
            <div className="sticky top-6">
              <JobDetailSidebar job={job} user={user} authLoading={authLoading} existingApplication={existingApplication} isSaved={isSaved(job.id)} onToggleSave={() => toggleSave.mutate(job.id)} />
            </div>
          </motion.div>
        </div>
      ) : (
        /* Compact single-column layout for lean crawler jobs */
        <div className="max-w-2xl mx-auto space-y-5">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="p-6">
                <JobDetailHeader job={job} />
              </CardContent>
            </Card>
          </motion.div>

          {/* Description as protagonist */}
          {job.description && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <JobDetailContent job={job} />
            </motion.div>
          )}

          {/* CTA right after description for lean jobs */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <JobDetailSidebar job={job} user={user} authLoading={authLoading} existingApplication={existingApplication} isSaved={isSaved(job.id)} onToggleSave={() => toggleSave.mutate(job.id)} />
          </motion.div>
        </div>
      )}

      {/* Sticky mobile CTA */}
      <StickyMobileCTA job={job} user={user} authLoading={authLoading} existingApplication={existingApplication} />
    </div>
  );
}
